<!--
  @component AuthModal
  @description Authentication modal for Spectral Cloud login and library selection
-->
<script lang="ts">
  import { onDestroy } from 'svelte';
  import { t } from '@/i18n';
  import { isAuthModalVisible, hideAuthModal } from '@stores/uiStore';
  import {
    isAuthLoading,
    authError,
    loginViaProxy,
    loginToSupabase,
    refreshProxyAuth,
    clearAuthError,
    isSupabaseAuthenticated,
    isZoteroConfigured,
  } from '@stores/authStore';
  import { allLibraries, selectedLibrary, selectLibrary } from '@stores/libraryStore';
  import { api } from '@/config';
  import { trapFocus } from '@/utils';
  import type { StoredLibrary } from '@types';

  // Focus trap management
  let modalElement: HTMLDivElement;
  let releaseFocusTrap: (() => void) | null = null;

  // Activate focus trap when modal opens
  $: if ($isAuthModalVisible && modalElement) {
    releaseFocusTrap = trapFocus(modalElement, { autoFocus: true });
  }

  // Local state
  let emailInput = '';
  let passwordInput = '';
  let step: 'auth' | 'library-select' = 'auth';

  // Get Portal URL with return parameter
  function getPortalUrl(path: string): string {
    const portalUrl = api.proxy.portalUrl;
    const returnTo = encodeURIComponent(window.location.href.split('#')[0]);
    return `${portalUrl}/?returnTo=${returnTo}#${path}`;
  }

  // Handle login via proxy
  async function handleProxyLogin() {
    const result = await loginViaProxy();
    if (result.success) {
      step = 'library-select';
    }
  }

  // Handle Supabase login
  async function handleSupabaseLogin() {
    if (!emailInput.trim() || !passwordInput.trim()) return;
    await loginToSupabase(emailInput, passwordInput);
  }

  // Redirect to Portal for login/configuration
  function redirectToPortal(path: string = '/settings') {
    window.location.href = getPortalUrl(path);
  }

  // Retry proxy authentication check
  async function handleRetryProxy() {
    await refreshProxyAuth();
  }

  // Handle library selection
  function handleSelectLibrary(library: StoredLibrary) {
    selectLibrary(library);
    hideAuthModal();
    step = 'auth';
  }

  // Handle keydown
  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !$isSupabaseAuthenticated) {
      handleSupabaseLogin();
    } else if (event.key === 'Escape') {
      hideAuthModal();
    }
  }

  // Reset on close
  function handleClose() {
    releaseFocusTrap?.();
    releaseFocusTrap = null;
    hideAuthModal();
    clearAuthError();
    step = 'auth';
    emailInput = '';
    passwordInput = '';
  }

  // Clean up focus trap on destroy
  onDestroy(() => {
    releaseFocusTrap?.();
  });
</script>

{#if $isAuthModalVisible}
  <div class="modal-overlay" on:keydown={handleKeydown} role="presentation">
    <button
      type="button"
      class="modal-backdrop"
      on:click={handleClose}
      aria-label={$t('common.close')}
      tabindex="-1"
    ></button>
    <div
      bind:this={modalElement}
      class="modal-content"
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
    >
      <div class="modal-header">
        <h2 id="auth-modal-title" class="modal-title">{$t('auth.title')}</h2>
        <button class="close-button" on:click={handleClose} aria-label={$t('common.close')}>
          ×
        </button>
      </div>

      {#if $authError}
        <div class="error-message" role="alert">
          {$authError}
          <button class="error-close" on:click={clearAuthError} aria-label={$t('common.close')}
            >×</button
          >
        </div>
      {/if}

      {#if step === 'auth'}
        <!-- Authentication via Spectral Cloud -->
        <div class="modal-body">
          {#if $isAuthLoading}
            <div class="proxy-status">
              <span class="loading-spinner"></span>
              <p class="proxy-message">Vérification de l'authentification...</p>
            </div>
          {:else if !$isSupabaseAuthenticated}
            <div class="proxy-login">
              <p class="proxy-message">Connectez-vous avec votre compte Spectral Cloud.</p>

              <div class="field-group">
                <label class="field-label" for="email">Email</label>
                <input
                  id="email"
                  class="field-input"
                  type="email"
                  placeholder="votre@email.com"
                  bind:value={emailInput}
                  on:keydown={handleKeydown}
                  disabled={$isAuthLoading}
                />
              </div>

              <div class="field-group">
                <label class="field-label" for="password">Mot de passe</label>
                <input
                  id="password"
                  class="field-input"
                  type="password"
                  placeholder="••••••••"
                  bind:value={passwordInput}
                  on:keydown={handleKeydown}
                  disabled={$isAuthLoading}
                />
              </div>

              <div class="action-buttons">
                <button
                  class="action-button primary"
                  on:click={handleSupabaseLogin}
                  disabled={$isAuthLoading || !emailInput.trim() || !passwordInput.trim()}
                >
                  {#if $isAuthLoading}
                    <span class="loading-spinner"></span>
                  {:else}
                    Se connecter
                  {/if}
                </button>
              </div>

              <p class="proxy-hint">
                Pas encore de compte ? <a href={getPortalUrl('/auth/register')}>Créer un compte</a>
              </p>
            </div>
          {:else if !$isZoteroConfigured}
            <div class="proxy-status">
              <p class="proxy-message success">✓ Connecté à Spectral Cloud</p>
              <p class="proxy-instruction">
                Votre clé API Zotero n'est pas encore configurée. Ajoutez-la dans les paramètres du
                portail.
              </p>
              <div class="action-buttons">
                <button
                  class="action-button primary"
                  on:click={() => redirectToPortal('/settings')}
                >
                  Configurer la clé API
                </button>
                <button class="action-button secondary" on:click={handleRetryProxy}>
                  Réessayer
                </button>
              </div>
            </div>
          {:else}
            <div class="proxy-status">
              <p class="proxy-message success">
                ✓ Connecté à Spectral Cloud<br />
                ✓ Clé API Zotero configurée
              </p>
              <div class="action-buttons">
                <button
                  class="action-button primary"
                  on:click={handleProxyLogin}
                  disabled={$isAuthLoading}
                >
                  {#if $isAuthLoading}
                    <span class="loading-spinner"></span>
                  {:else}
                    Charger les bibliothèques
                  {/if}
                </button>
              </div>
            </div>
          {/if}
        </div>
      {:else}
        <!-- Library Selection -->
        <div class="modal-body">
          <div class="field-group">
            <p id="library-select-label" class="field-label">{$t('auth.selectLibrary')}</p>
            <p id="library-select-help" class="field-help">{$t('auth.selectLibraryHelp')}</p>

            <div
              class="library-list"
              role="listbox"
              aria-labelledby="library-select-label"
              aria-describedby="library-select-help"
            >
              {#each $allLibraries as library (library.id + library.type)}
                <button
                  class="library-item"
                  class:selected={$selectedLibrary?.id === library.id &&
                    $selectedLibrary?.type === library.type}
                  on:click={() => handleSelectLibrary(library)}
                  role="option"
                  aria-selected={$selectedLibrary?.id === library.id &&
                    $selectedLibrary?.type === library.type}
                >
                  <span class="library-icon" aria-hidden="true">
                    {library.type === 'user' ? '👤' : '👥'}
                  </span>
                  <span class="library-name">{library.name}</span>
                  {#if $selectedLibrary?.id === library.id && $selectedLibrary?.type === library.type}
                    <span class="library-check" aria-hidden="true">✓</span>
                  {/if}
                </button>
              {/each}

              {#if $allLibraries.length === 0}
                <div class="no-libraries">{$t('auth.noGroups')}</div>
              {/if}
            </div>
          </div>
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
  }

  .modal-backdrop {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    border: none;
    cursor: default;
    animation: fadeIn 0.2s ease-out;
  }

  .modal-content {
    position: relative;
    background: var(--color-bg-secondary, #383838);
    border-radius: 8px;
    width: 450px;
    max-width: 90vw;
    max-height: 80vh;
    overflow: hidden;
    border: 1px solid var(--color-border, #555);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    animation: slideIn 0.2s ease-out;
    display: flex;
    flex-direction: column;
  }

  .modal-header {
    padding: 16px 20px;
    border-bottom: 1px solid var(--color-border, #555);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .modal-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }

  .close-button {
    background: none;
    border: none;
    color: var(--color-text-secondary, #aaa);
    font-size: 24px;
    cursor: pointer;
    padding: 0;
    line-height: 1;
  }

  .close-button:hover {
    color: var(--color-text-primary, #e8e8e8);
  }

  .modal-body {
    padding: 20px;
    overflow-y: auto;
  }

  .field-group {
    margin-bottom: 16px;
  }

  .field-label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: var(--color-text-secondary, #bbb);
    margin-bottom: 6px;
  }

  .field-input {
    width: 100%;
    padding: 10px 12px;
    background: var(--color-bg-primary, #2b2b2b);
    border: 1px solid var(--color-border, #555);
    border-radius: 4px;
    color: var(--color-text-primary, #e8e8e8);
    font-size: 14px;
    box-sizing: border-box;
  }

  .field-input:focus {
    outline: none;
    border-color: var(--color-accent, #5c7cfa);
  }

  .field-input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .field-help {
    margin: 8px 0 0;
    font-size: 12px;
    color: var(--color-text-muted, #888);
    line-height: 1.4;
  }

  .action-buttons {
    display: flex;
    gap: 8px;
    margin-top: 20px;
  }

  .action-button {
    flex: 1;
    padding: 10px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    transition: background-color 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .action-button.primary {
    background: var(--color-accent, #5c7cfa);
    color: white;
  }

  .action-button.primary:hover:not(:disabled) {
    background: var(--color-accent-hover, #4c6ef5);
  }

  .action-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .library-list {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid var(--color-border, #555);
    border-radius: 4px;
    background: var(--color-bg-primary, #2b2b2b);
    margin-top: 8px;
  }

  .library-item {
    width: 100%;
    padding: 12px 16px;
    cursor: pointer;
    border: none;
    border-bottom: 1px solid var(--color-border-light, #444);
    display: flex;
    align-items: center;
    gap: 10px;
    background: transparent;
    color: var(--color-text-primary, #e8e8e8);
    font-size: 13px;
    text-align: left;
    transition: background-color 0.2s;
  }

  .library-item:hover {
    background: var(--color-bg-hover, #333);
  }

  .library-item:last-child {
    border-bottom: none;
  }

  .library-item.selected {
    background: var(--color-bg-selected, #4a4a4a);
    border-left: 3px solid var(--color-accent, #5c7cfa);
  }

  .library-icon {
    font-size: 18px;
  }

  .library-name {
    flex: 1;
  }

  .library-check {
    color: var(--color-accent, #5c7cfa);
    font-weight: bold;
  }

  .no-libraries {
    padding: 20px;
    text-align: center;
    color: var(--color-text-muted, #888);
    font-style: italic;
  }

  .error-message {
    background: var(--color-danger, #dc3545);
    color: white;
    padding: 10px 16px;
    margin: 0 20px;
    margin-top: 16px;
    border-radius: 4px;
    font-size: 13px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .error-close {
    background: none;
    border: none;
    color: white;
    font-size: 18px;
    cursor: pointer;
    padding: 0;
    line-height: 1;
  }

  .loading-spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 1s linear infinite;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* Proxy mode styles */
  .proxy-status {
    text-align: center;
    padding: 16px 0;
  }

  .proxy-login {
    padding: 8px 0;
  }

  .proxy-message {
    font-size: 14px;
    color: var(--color-text-secondary, #ccc);
    margin-bottom: 16px;
    line-height: 1.5;
  }

  .proxy-message.success {
    color: var(--color-success, #51cf66);
  }

  .proxy-instruction {
    font-size: 13px;
    color: var(--color-text-muted, #999);
    margin-bottom: 16px;
  }

  .proxy-hint {
    text-align: center;
    font-size: 12px;
    color: var(--color-text-muted, #888);
    margin-top: 16px;
  }

  .proxy-hint a {
    color: var(--color-accent, #5c7cfa);
    text-decoration: none;
  }

  .proxy-hint a:hover {
    text-decoration: underline;
  }

  .action-button.secondary {
    background: var(--color-bg-tertiary, #555);
    color: var(--color-text-primary, #e8e8e8);
  }

  .action-button.secondary:hover:not(:disabled) {
    background: var(--color-bg-hover, #666);
  }
</style>
